import React, { ReactNode, useId, useState } from 'react';
import { Box, ButtonBase, SxProps, Theme } from '@mui/material';
import ScanFrame from './ScanFrame';
import PanelHeader from './PanelHeader';
import Chevron from './Chevron';

/** The standard named panel: `ScanFrame`'s flat hairline box plus
 * `PanelHeader`'s title. Every panel in the app is one of these two
 * components — that's what keeps a vault row, the killboard, and a
 * settings card reading as one instrument. `sx` is an escape hatch onto
 * ScanFrame's own root; `corners` is accepted only so existing call sites
 * keep compiling (ScanFrame no longer draws corner ornament). `scrollBody`
 * turns the panel into a flex column whose body scrolls independently while
 * the header stays put — for a panel a CSS Grid sibling stretches to a
 * height its own content may exceed (see SystemIntelPanel's Killboard,
 * height-matched to the Effect & Bonuses / Possible Connections column). */
const ConsolePanel: React.FC<{
  title: string;
  accent: string;
  children: ReactNode;
  action?: ReactNode;
  sx?: SxProps<Theme>;
  corners?: boolean;
  scrollBody?: boolean;
  collapsible?: boolean;
}> = ({ title, accent, children, action, sx, corners, scrollBody, collapsible }) => {
  const [open, setOpen] = useState(true);
  const bodyId = useId();
  return (
  <ScanFrame
    color={accent}
    corners={corners}
    sx={{
      display: scrollBody ? 'flex' : 'block',
      flexDirection: 'column',
      px: { xs: 2, md: 2.5 },
      py: { xs: 1.5, md: 2 },
      ...sx,
    }}
  >
    {collapsible ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ButtonBase
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={bodyId}
          sx={{ flex: 1, minWidth: 0, display: 'block', textAlign: 'left', '&.Mui-focusVisible': { outline: `2px solid ${accent}` } }}
        >
          <PanelHeader
            label={title}
            accent={accent}
            action={<Chevron color={accent} size={8} sx={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }} />}
            sx={{ px: 0 }}
          />
        </ButtonBase>
        {action}
      </Box>
    ) : (
      <PanelHeader label={title} accent={accent} action={action} sx={{ px: 0, flexShrink: 0 }} />
    )}
    <Box id={bodyId} hidden={collapsible && !open} sx={{ pt: 2, ...(scrollBody ? { flex: 1, minHeight: 0, overflowY: 'auto' } : {}) }}>{children}</Box>
  </ScanFrame>
);
};

export default ConsolePanel;
